/**
 * Cookie Consent Service
 * Gère le consentement des cookies et le chargement conditionnel des scripts tiers.
 * 
 * Catégories : Performance, Personalization, Advertising
 * Clé localStorage : authorizationCookies
 * 
 * Chemin : public/ressources/script/cookieConsent.js
 */

const STORAGE_KEY = 'authorizationCookies'

// Utilise window pour garantir une seule instance partagée
if (!window.__cookieConsent) {
  window.__cookieConsent = {
    registeredScripts: {
      Performance: [],
      Personalization: [],
      Advertising: [],
    },
    loadedScripts: {
      Performance: false,
      Personalization: false,
      Advertising: false,
    },
  }
}

const state = window.__cookieConsent

/**
 * Retourne le consentement sauvegardé ou null
 */
export function getConsent() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? JSON.parse(saved) : null
  } catch {
    return null
  }
}

/**
 * Vérifie si une catégorie est acceptée
 * @param {string} category - 'Performance' | 'Personalization' | 'Advertising'
 */
export function isAccepted(category) {
  const consent = getConsent()
  if (!consent) return false
  return consent[category] === true
}

/**
 * Initialise le Google Consent Mode par défaut (tout denied)
 * Appeler une seule fois dans App.vue mounted()
 */
export function initConsentMode() {
  window.dataLayer = window.dataLayer || []
  function gtag() { window.dataLayer.push(arguments) }
  window.gtag = gtag

  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
  })

  const consent = getConsent()
  if (consent) {
    updateGoogleConsent(consent)
  }
}

/**
 * Met à jour le Google Consent Mode dynamiquement
 */
function updateGoogleConsent(consent) {
  if (!window.gtag) return

  window.gtag('consent', 'update', {
    analytics_storage: consent.Performance ? 'granted' : 'denied',
    functionality_storage: consent.Personalization ? 'granted' : 'denied',
    personalization_storage: consent.Personalization ? 'granted' : 'denied',
    ad_storage: consent.Advertising ? 'granted' : 'denied',
    ad_user_data: consent.Advertising ? 'granted' : 'denied',
    ad_personalization: consent.Advertising ? 'granted' : 'denied',
  })
}

/**
 * Enregistre une fonction à exécuter quand une catégorie est acceptée.
 * @param {string} category - 'Performance' | 'Personalization' | 'Advertising'
 * @param {Function} loadFn - Fonction à exécuter (ex: charger un script GA, Pixel, etc.)
 * 
 * Exemple :
 *   registerScript('Performance', () => {
 *     const script = document.createElement('script')
 *     script.src = 'https://www.googletagmanager.com/gtag/js?id=G-XXXXXXX'
 *     script.async = true
 *     document.head.appendChild(script)
 *   })
 */
export function registerScript(category, loadFn) {
  if (!state.registeredScripts[category]) {
    console.warn(`[CookieConsent] Catégorie inconnue : ${category}`)
    return
  }
  state.registeredScripts[category].push(loadFn)
}

/**
 * Charge les scripts d'une catégorie si pas déjà chargés
 */
function loadCategoryScripts(category) {
  if (state.loadedScripts[category]) return

  state.registeredScripts[category].forEach(fn => {
    try {
      fn()
    } catch (err) {
      console.error(`[CookieConsent] Erreur au chargement du script (${category}):`, err)
    }
  })

  state.loadedScripts[category] = true
}

/**
 * Met à jour le consentement — appelé depuis cookie.vue
 * Sauvegarde dans localStorage + exécute les scripts autorisés
 * @param {Object} consent - { Essential: true, Performance: bool, Personalization: bool, Advertising: bool }
 */
export function updateConsent(consent) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  updateGoogleConsent(consent)

  const categories = ['Performance', 'Personalization', 'Advertising']
  categories.forEach(cat => {
    if (consent[cat]) {
      loadCategoryScripts(cat)
    }
  })
}

/**
 * Applique le consentement sauvegardé — appelé au mounted de App.vue
 * Exécute les scripts des catégories déjà acceptées
 */
export function applyConsent() {
  const consent = getConsent()
  if (!consent) return

  const categories = ['Performance', 'Personalization', 'Advertising']
  categories.forEach(cat => {
    if (consent[cat]) {
      loadCategoryScripts(cat)
    }
  })
}

/**
 * Réinitialise tout le consentement (utile pour debug)
 */
export function resetConsent() {
  localStorage.removeItem(STORAGE_KEY)
  Object.keys(state.loadedScripts).forEach(key => { state.loadedScripts[key] = false })
}
